import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, Validators, AbstractControl, FormGroup, ReactiveFormsModule, ValidationErrors, FormControl, FormArray } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogTitle, MatDialogContent } from '@angular/material/dialog';
import { RoleFormData } from '../../../../shared/models/role-form-data';
import { MatRadioModule } from '@angular/material/radio';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { Permission } from '../../../../shared/models/permission';
import { map, Observable, startWith, Subscription } from 'rxjs';
import { AdminService } from '../../services/admin';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatExpansionModule } from '@angular/material/expansion';

interface Group {
    id   : string;
    name : string;
    permissions: Permission[];
}

interface AssignedPermissionGroupFormValue {
    id: string;
    name: string;
    accessType: 'full' | 'granular';
    selectedGranularPermissions: string[];
}

@Component({
	selector: 'app-role-form',
	standalone: true,
	imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatSlideToggleModule, MatButtonModule, MatIconModule, MatDialogTitle, MatDialogContent, MatRadioModule, MatCheckboxModule, MatAutocompleteModule, MatExpansionModule],
	templateUrl: './role-form.html',
	styleUrl: './role-form.css'
})

export class RoleForm implements OnInit, OnDestroy {
	roleForm                 : FormGroup;
	isEditMode               : boolean = false;
	permissions              : Permission[] = [];
    groups                   : Group[] = [];
    filteredGroups! : Observable<Group[]>;

    groupFilterControl = new FormControl<string | Group>('');

	private subscriptions = new Subscription();

	constructor(private fb: FormBuilder, public dialogRef: MatDialogRef<RoleForm, RoleFormData | undefined>, @Inject(MAT_DIALOG_DATA) public data: RoleFormData | null, private adminService: AdminService) {
		this.isEditMode = !!this.data;
		
		this.roleForm = this.fb.group({
			id             : [{value: this.data?.id || '', disabled: this.isEditMode}, [Validators.required]],
			name           : [this.data?.name || '', [Validators.required]],
			description    : [this.data?.description || ''],
			active         : [this.data?.active ?? true],
			accessType     : ['global'],
            assignedGroups : this.fb.array<FormGroup>([])
		});

        this.subscriptions.add(
            this.accessTypeControl?.valueChanges.subscribe(value => {
                if(value === 'global') {
                    this.assignedGroupsControl.clear();
                } else {
                    if(this.isEditMode && this.data?.permissions && !this.data.permissions.includes('*.*')) {
                        this.initializePermissionsForm(this.data.permissions.filter(p => p !== '*.*'));
                    } else {
                        this.assignedGroupsControl.clear();
                    }
                }
            })
        );

        this.groupFilterControl = new FormControl<string | Group>('', RoleForm.groupObjectValidator);
	}

    ngOnInit(): void {
        this.loadPermissions();

        this.filteredGroups = this.groupFilterControl.valueChanges.pipe(startWith(''), map(value => {
            const searchString = typeof value === 'string' ? value : (value as Group)?.name || '';

            return searchString ? this._filterGroups(searchString) : this.getAvailableGroupsForSelection();
        }));
    }

    static groupObjectValidator(control: AbstractControl): ValidationErrors | null {
        const value = control.value;

        if(value && typeof value === 'string' && value.trim() !== '') {
            return { notAGroupObject: true };
        }

        return null;
    }

    displayGroupName(group: Group | string): string {
        if(typeof group === 'object' && group !== null && 'name' in group) {
            return group.name;
        }

        return String(group || '');
    }

    async loadPermissions(): Promise<void> {
		const result = await this.adminService.getAllPermissions();

		if(result) {
			this.permissions = result;
            
            this.processPermissionsIntoGroups();

            if(this.isEditMode && this.data?.permissions && this.data.permissions.length > 0) {
                if(this.data.permissions.includes('*.*')) {
                    this.accessTypeControl?.setValue('global', {emitEvent: false});
                } else {
                    this.accessTypeControl?.setValue('custom', {emitEvent: false});
                    this.initializePermissionsForm(this.data.permissions);
                }
            } else if(!this.isEditMode) {
                this.accessTypeControl?.setValue('global');
            }
		}
	}

    processPermissionsIntoGroups(): void {
        const groupsMap = new Map<string, Group>();

        this.permissions.forEach(perm => {
            const [resource, action] = perm.id.split('.');

            if(action === '*') return;

            const groupId   = resource;
            const groupName = perm.group;

            if(!groupsMap.has(groupId)) {
                groupsMap.set(groupId, { id: groupId, name: groupName, permissions: [] });
            }

            groupsMap.get(groupId)!.permissions.push(perm);
        });

        this.groups = Array.from(groupsMap.values()).sort((a, b) => a.name.localeCompare(b.name));
        
        this.groupFilterControl.updateValueAndValidity({ emitEvent: false });
    }
    
    private _filterGroups(value: string): Group[] {
        const filterValue = value.toLowerCase();

        return this.getAvailableGroupsForSelection().filter(group => group.name.toLowerCase().includes(filterValue));
    }

    getAvailableGroupsForSelection(): Group[] {
        const assignedGroupIds = new Set(this.assignedGroupsControl.controls.map(g => (g as FormGroup).get('id')?.value));

        return this.groups.filter(group => !assignedGroupIds.has(group.id));
    }

    onAddGroup(): void {
        if(this.groupFilterControl.invalid) {
            this.groupFilterControl.markAsTouched();

            return;
        }

        const group = this.groupFilterControl.value as Group;

        if(group && !this.assignedGroupsControl.controls.some(g => (g as FormGroup).get('id')?.value === group.id)) {
            this.assignedGroupsControl.insert(0, this.fb.group({
                id: [group.id],
                name: [group.name],
                accessType: ['full'], 
                selectedGranularPermissions: this.fb.array<string>([])
            }));

            this.groupFilterControl.setValue('');
            this.groupFilterControl.markAsUntouched();
            this.groupFilterControl.markAsPristine();
        }
    }

    onRemoveGroup(index: number): void {
        this.assignedGroupsControl.removeAt(index);
    }

    getGranularPermissionsForGroup(groupId: string): Permission[] {
        const group = this.groups.find(g => g.id === groupId);

        return group ? group.permissions.filter(p => p.id.startsWith(`${groupId}.`) && p.id.indexOf('*') === -1) : [];
    }

    onGranularPermissionChange(formArray: FormArray, permissionId: string, isChecked: boolean): void {
        const currentPermissions: string[] = formArray.value;

        if(isChecked) {
            if(!currentPermissions.includes(permissionId)) {
                formArray.push(new FormControl(permissionId));
            }
        } else {
            const index = currentPermissions.indexOf(permissionId);

            if(index > -1) {
                formArray.removeAt(index);
            }
        }
    }

	getGranularPermissionsFormArray(groupControl: AbstractControl): FormArray {
        return (groupControl as FormGroup).get('selectedGranularPermissions') as FormArray;
    }

    initializePermissionsForm(rolePermissions: string[]): void {
        this.assignedGroupsControl.clear();

        const permissionsByGroup: { [groupId: string]: string[] } = {};

        rolePermissions.forEach(permId => {
            if(permId === '*.*') {
                return;
            }

            const [resource, action] = permId.split('.');

            if(!permissionsByGroup[resource]) {
                permissionsByGroup[resource] = [];
            }

            permissionsByGroup[resource].push(permId);
        });

        for (const groupId in permissionsByGroup) {
            const assignedPerms = permissionsByGroup[groupId];
            const groupDefinition = this.groups.find(g => g.id === groupId);

            if(!groupDefinition) {
                continue;
            }

            const isFullAccess = assignedPerms.includes(`${groupId}.*`);
            const granularPerms = assignedPerms.filter(p => p !== `${groupId}.*`);

            this.assignedGroupsControl.push(this.fb.group({
                id: [groupId],
                name: [groupDefinition.name],
                accessType: [isFullAccess ? 'full' : 'granular'],
                selectedGranularPermissions: this.fb.array(granularPerms.map(p => new FormControl(p)))
            }));
        }
    }

    get idControl() {
		return this.roleForm.get('id');
	}

	get nameControl() {
		return this.roleForm.get('name');
	}

    get accessTypeControl() {
        return this.roleForm.get('accessType');
    }

    get assignedGroupsControl(): FormArray {
        return this.roleForm.get('assignedGroups') as FormArray;
    }

	onSubmit(): void {
		this.roleForm.markAllAsTouched();

		if(this.roleForm.valid) {
            const formData = this.roleForm.getRawValue();
            let finalPermissions: string[] = [];

            if(formData.accessType == 'global') {
                finalPermissions = ['*.*'];
            } else {
                formData.assignedGroups.forEach((group: any) => {
                    if(group.accessType === 'full') {
                        finalPermissions.push(`${group.id}.*`);
                    } else {
                        finalPermissions = finalPermissions.concat(group.selectedGranularPermissions);
                    }
                });
            }

            const roleDataToSend: RoleFormData = {
                id          : formData.id,
                name        : formData.name,
                description : formData.description,
                active      : formData.active,
                permissions : finalPermissions
            }

			this.dialogRef.close(roleDataToSend);
		}
	}

	onCancel(): void {
		this.dialogRef.close(undefined);
	}

	ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
}